import { ConfigureUomDrawer } from "@/components/products/configure-uom-drawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMediaQuery } from "@/hooks/use-media-query";
import { ApiError } from "@/lib/api/api-error";
import { cn } from "@/lib/utils";
import { ProductService } from "@/services/product-service";
import type {
  ConfigureProductUomPayload,
  Product,
  StockAdjustmentBatchCreatePayload,
  StockAdjustmentCreatePayload,
  StockAdjustmentMode,
  StockAdjustmentReason,
  StockLedgerEntry,
  UnitOfMeasure,
  Warehouse,
} from "@/services/types/products";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

export type ProductDrawerTab = "stock" | "units" | "history";

const REASONS: { value: StockAdjustmentReason; label: string }[] = [
  { value: "stocktake_correction", label: "Stocktake correction" },
  { value: "damage_wastage", label: "Damage / wastage" },
  { value: "found_stock", label: "Found stock" },
  {
    value: "transfer_between_warehouses",
    label: "Transfer between warehouses",
  },
  { value: "supplier_return", label: "Supplier return" },
  { value: "data_correction", label: "Data correction" },
  { value: "opening_balance", label: "Opening balance" },
];

const HISTORY_SOURCES = [
  ["all", "All sources"],
  ["manual", "Manual adjustment"],
  ["upload", "Excel upload"],
  ["erp", "ERP sync"],
  ["order", "Order"],
  ["transfer", "Transfer"],
  ["opening_balance", "Opening balance"],
  ["uom_change", "UOM change"],
] as const;

const MOBILE_HISTORY_SOURCES = [
  ["all", "All sources"],
  ["manual", "Manual"],
  ["upload", "Uploads"],
  ["order", "Orders"],
] as const;

function numberValue(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function quantizeStockQuantity(value: number): number {
  const quantized = Number(value.toFixed(2));
  return Object.is(quantized, -0) ? 0 : quantized;
}

function formatQuantity(value: number | null | undefined): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(numberValue(value));
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (
    !(error instanceof ApiError) ||
    typeof error.body !== "object" ||
    !error.body
  ) {
    return fallback;
  }
  const findMessage = (value: unknown): string | null => {
    if (typeof value === "string") {
      return value;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        const message = findMessage(item);
        if (message) {
          return message;
        }
      }
    }
    if (typeof value === "object" && value) {
      for (const item of Object.values(value)) {
        const message = findMessage(item);
        if (message) {
          return message;
        }
      }
    }
    return null;
  };
  const body = error.body as Record<string, unknown>;
  const message = findMessage(body.errors ?? body.detail ?? body);
  if (message) {
    return message;
  }
  return fallback;
}

type StockScopeRow = {
  key: string;
  warehouse: Warehouse;
  productBrandId?: number | null;
  label: string;
  current: number;
  reserved: number;
};

function ProductHeading({ product }: { product: Product }) {
  const storageLabel =
    product.storage_type === "cold"
      ? "Chilled"
      : product.storage_type === "dry"
        ? "Dry"
        : "General";
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <SheetTitle className="font-medium text-lg">
          {product.name || "Unnamed product"}
        </SheetTitle>
        <Badge className="rounded-full bg-cyan-50 px-2 py-0.5 font-medium text-[11px] text-cyan-800 hover:bg-cyan-50">
          {storageLabel}
        </Badge>
        {product.product_brands.length > 0 ? (
          <Badge
            variant="secondary"
            className="rounded px-2 py-0.5 text-[11px]"
          >
            {product.product_brands.length} brand
            {product.product_brands.length === 1 ? "" : "s"}
          </Badge>
        ) : null}
      </div>
      <SheetDescription className="text-xs">
        SKU {product.sku} · Base unit {product.base_stock_uom_code || "—"} ·{" "}
        {formatQuantity(product.quantity_available)}{" "}
        {product.base_stock_uom_code || "units"} in stock
      </SheetDescription>
    </div>
  );
}

function StockTab({
  product,
  warehouses,
  uoms,
  canManage,
  onAdjusted,
  onCancel,
}: {
  product: Product;
  warehouses: Warehouse[];
  uoms: UnitOfMeasure[];
  canManage: boolean;
  onAdjusted: (product: Product) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<StockAdjustmentMode>("set_count");
  const [uomId, setUomId] = useState("");
  const [reason, setReason] = useState<StockAdjustmentReason | "">("");
  const [note, setNote] = useState("");
  const [groupBy, setGroupBy] = useState<"warehouse" | "brand">("warehouse");
  const [values, setValues] = useState<Record<string, string>>({});
  const [touchedRowKeys, setTouchedRowKeys] = useState<Set<string>>(new Set());

  const stockWarehouses = useMemo(
    () =>
      warehouses.filter(
        (warehouse) => warehouse.is_active && warehouse.is_stock_location,
      ),
    [warehouses],
  );
  const allowedUomIds = useMemo(
    () =>
      new Set([
        product.base_stock_uom,
        ...product.uom_conversions
          .filter((conversion) => conversion.is_active)
          .map((conversion) => conversion.from_uom),
      ]),
    [product],
  );
  const allowedUoms = uoms.filter((uom) => allowedUomIds.has(uom.id));
  const selectedUomFactor = useMemo(() => {
    const selectedUomId = Number(uomId);
    if (!selectedUomId || selectedUomId === product.base_stock_uom) {
      return 1;
    }
    return (
      numberValue(
        product.uom_conversions.find(
          (conversion) =>
            conversion.is_active &&
            conversion.from_uom === selectedUomId &&
            conversion.to_uom === product.base_stock_uom,
        )?.factor,
      ) || 1
    );
  }, [product, uomId]);

  // Product changes must reset any in-progress stock adjustment draft.
  // biome-ignore lint/correctness/useExhaustiveDependencies: product identity is an intentional reset boundary.
  useEffect(() => {
    setUomId(product.base_stock_uom ? String(product.base_stock_uom) : "");
    setMode("set_count");
    setReason("");
    setNote("");
    setGroupBy("warehouse");
    setValues({});
    setTouchedRowKeys(new Set());
  }, [product.id, product.base_stock_uom]);

  const mutation = useMutation({
    mutationFn: async (
      request:
        | { kind: "aggregate"; payload: StockAdjustmentCreatePayload }
        | { kind: "scoped"; payload: StockAdjustmentBatchCreatePayload },
    ) => {
      if (request.kind === "scoped") {
        await ProductService.createStockAdjustmentBatch(request.payload);
      } else {
        await ProductService.createStockAdjustment(request.payload);
      }
    },
    onSuccess: async () => {
      const refreshedProduct = await ProductService.retrieve(product.id);
      toast.success("Stock levels updated.");
      onAdjusted(refreshedProduct);
      setValues({});
      setReason("");
      setNote("");
      setTouchedRowKeys(new Set());
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["inventory-products"] }),
        queryClient.invalidateQueries({
          queryKey: ["product-stock-history", product.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["inventory-product", product.id],
        }),
      ]);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update stock levels."));
    },
  });

  const activeBrands = product.product_brands.filter(
    (brand) => brand.is_active,
  );
  const usesScopedRows =
    activeBrands.length > 0 || product.brand_warehouse_stocks.length > 0;
  const rows: StockScopeRow[] = stockWarehouses.flatMap((warehouse) => {
    if (!usesScopedRows) {
      const aggregate = product.warehouse_stocks.find(
        (stock) => stock.warehouse_id === warehouse.id,
      );
      return [
        {
          key: `${warehouse.id}:aggregate`,
          warehouse,
          label: warehouse.name,
          current: numberValue(aggregate?.quantity_available),
          reserved: numberValue(aggregate?.quantity_reserved),
        },
      ];
    }
    const brandRows = activeBrands.map((productBrand) => {
      const stock = product.brand_warehouse_stocks.find(
        (item) =>
          item.warehouse_id === warehouse.id &&
          item.product_brand_id === productBrand.id,
      );
      return {
        key: `${warehouse.id}:brand:${productBrand.id}`,
        warehouse,
        productBrandId: productBrand.id,
        label: productBrand.brand_name,
        current: numberValue(stock?.quantity_available),
        reserved: numberValue(stock?.quantity_reserved),
      };
    });
    const aggregate = product.warehouse_stocks.find(
      (stock) => stock.warehouse_id === warehouse.id,
    );
    const warehouseBrandStocks = product.brand_warehouse_stocks.filter(
      (stock) => stock.warehouse_id === warehouse.id && !stock.is_unallocated,
    );
    const unallocated = product.brand_warehouse_stocks.find(
      (item) => item.warehouse_id === warehouse.id && item.is_unallocated,
    );
    const inferredUnallocatedAvailable = quantizeStockQuantity(
      numberValue(aggregate?.quantity_available) -
        warehouseBrandStocks.reduce(
          (total, stock) => total + numberValue(stock.quantity_available),
          0,
        ),
    );
    const inferredUnallocatedReserved = quantizeStockQuantity(
      numberValue(aggregate?.quantity_reserved) -
        warehouseBrandStocks.reduce(
          (total, stock) => total + numberValue(stock.quantity_reserved),
          0,
        ),
    );
    return [
      ...brandRows,
      {
        key: `${warehouse.id}:unallocated`,
        warehouse,
        productBrandId: null,
        label: "Unbranded stock",
        current: unallocated
          ? numberValue(unallocated.quantity_available)
          : inferredUnallocatedAvailable,
        reserved: unallocated
          ? numberValue(unallocated.quantity_reserved)
          : inferredUnallocatedReserved,
      },
    ];
  });
  const changedRows = rows.filter((row) => {
    const value = values[row.key];
    return (
      touchedRowKeys.has(row.key) &&
      value !== undefined &&
      value.trim() !== "" &&
      Number.isFinite(Number(value)) &&
      (mode === "adjust_by" ||
        Number(value) * selectedUomFactor !== row.current)
    );
  });
  const getNextQuantity = (row: StockScopeRow) => {
    if (!touchedRowKeys.has(row.key)) {
      return row.current;
    }
    const rawValue = values[row.key];
    const input = Number(rawValue);
    if (!Number.isFinite(input) || rawValue?.trim() === "") {
      return row.current;
    }
    const normalizedInput = input * selectedUomFactor;
    return mode === "set_count"
      ? normalizedInput
      : row.current + normalizedInput;
  };
  const getRowError = (row: StockScopeRow) => {
    if (!touchedRowKeys.has(row.key)) {
      return null;
    }
    const nextQuantity = getNextQuantity(row);
    if (nextQuantity < 0) {
      return "Stock cannot be negative.";
    }
    if (nextQuantity < row.reserved) {
      return `Cannot reduce below ${formatQuantity(row.reserved)} reserved.`;
    }
    return null;
  };
  const previewTotal = rows.reduce((total, row) => {
    if (!touchedRowKeys.has(row.key)) {
      return total + row.current;
    }
    const input = Number(values[row.key]);
    if (!Number.isFinite(input) || values[row.key]?.trim() === "") {
      return total + row.current;
    }
    const normalizedInput = input * selectedUomFactor;
    return (
      total +
      (mode === "set_count" ? normalizedInput : row.current + normalizedInput)
    );
  }, 0);
  const currentTotal = rows.reduce((total, row) => total + row.current, 0);
  const previewDelta = previewTotal - currentTotal;
  const groupedRows = useMemo(() => {
    const groups = new Map<string, { title: string; rows: StockScopeRow[] }>();
    for (const row of rows) {
      const key =
        groupBy === "warehouse"
          ? `warehouse:${row.warehouse.id}`
          : `brand:${row.productBrandId === undefined ? "aggregate" : (row.productBrandId ?? "unallocated")}`;
      const title = groupBy === "warehouse" ? row.warehouse.name : row.label;
      const group = groups.get(key) ?? { title, rows: [] };
      group.rows.push(row);
      groups.set(key, group);
    }
    return [...groups.entries()].map(([key, group]) => ({ key, ...group }));
  }, [groupBy, rows]);
  const conversionHelp = product.uom_conversions
    .filter(
      (conversion) =>
        conversion.is_active &&
        conversion.to_uom === product.base_stock_uom &&
        conversion.from_uom !== conversion.to_uom,
    )
    .map(
      (conversion) =>
        `1 ${conversion.from_uom_code} = ${formatQuantity(conversion.factor)} ${conversion.to_uom_code}`,
    )
    .join(" · ");

  const changeMode = (nextMode: StockAdjustmentMode) => {
    setMode(nextMode);
    setValues({});
    setTouchedRowKeys(new Set());
  };

  const changeUom = (nextUomId: string) => {
    setUomId(nextUomId);
    setValues({});
    setTouchedRowKeys(new Set());
  };

  const submit = () => {
    if (!uomId || !reason || changedRows.length === 0) {
      toast.error("Select a UOM, reason, and enter at least one stock value.");
      return;
    }
    const invalidRow = changedRows.find((row) => getRowError(row));
    if (invalidRow) {
      toast.error(getRowError(invalidRow) || "Enter a valid stock quantity.");
      return;
    }
    const payload = {
      operation_id: crypto.randomUUID(),
      product_id: product.id,
      mode,
      entered_uom_id: Number(uomId),
      reason,
      note,
      lines: changedRows.map((row) => ({
        warehouse_id: row.warehouse.id,
        expected_quantity: row.current,
        value: Number(values[row.key]),
      })),
    };
    if (usesScopedRows) {
      mutation.mutate({
        kind: "scoped",
        payload: {
          ...payload,
          lines: changedRows.map((row) => ({
            warehouse_id: row.warehouse.id,
            product_brand_id: row.productBrandId ?? null,
            expected_quantity: row.current,
            value: Number(values[row.key]),
          })),
        },
      });
    } else {
      mutation.mutate({ kind: "aggregate", payload });
    }
  };

  if (!product.base_stock_uom) {
    return (
      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
        <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-5 py-5 sm:px-8">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-950">
            Configure this product&apos;s base UOM before adjusting stock.
          </div>
        </div>
        <div className="border-t px-5 py-4 sm:px-8">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={onCancel}
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div className="min-w-0 flex-1 space-y-5 overflow-y-auto overflow-x-hidden px-5 py-5 sm:px-8">
        <div className="space-y-1">
          <h3 className="font-medium text-lg">Adjust stock</h3>
          <p className="text-muted-foreground text-sm leading-snug">
            Set a true count or record a movement. Every change is written to
            this SKU&apos;s history.
          </p>
        </div>

        <div className="grid grid-cols-2 rounded-lg bg-muted/60 p-1">
          {(["set_count", "adjust_by"] as const).map((value) => (
            <Button
              key={value}
              type="button"
              variant="ghost"
              className={cn(
                "h-9 rounded-md text-sm",
                mode === value && "bg-background shadow-sm hover:bg-background",
              )}
              onClick={() => changeMode(value)}
            >
              {value === "set_count" ? "Set new count" : "±  Adjust by"}
            </Button>
          ))}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <label className="font-medium text-sm" htmlFor="stock-uom">
              Enter quantities in
            </label>
            <div className="flex-1" />
            <Select
              value={uomId}
              onValueChange={changeUom}
              disabled={!canManage}
            >
              <SelectTrigger id="stock-uom" className="h-10 w-44 rounded-lg">
                <SelectValue placeholder="Select UOM" />
              </SelectTrigger>
              <SelectContent>
                {allowedUoms.map((uom) => (
                  <SelectItem key={uom.id} value={String(uom.id)}>
                    {uom.name} ({uom.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-muted-foreground text-xs">
            {mode === "set_count"
              ? "Enter the physical count, not the movement."
              : "Enter the movement, not the total."}{" "}
            {conversionHelp}
          </p>
        </div>

        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm">
              Stock by {groupBy === "warehouse" ? "warehouse" : "brand"}
            </h4>
            <Badge
              variant="secondary"
              className="rounded px-2 py-0.5 text-[11px]"
            >
              {stockWarehouses.length}{" "}
              {stockWarehouses.length === 1 ? "warehouse" : "warehouses"}
            </Badge>
            {usesScopedRows ? (
              <div className="ml-auto flex rounded-md bg-muted/60 p-0.5">
                {(["warehouse", "brand"] as const).map((value) => (
                  <Button
                    key={value}
                    type="button"
                    size="sm"
                    variant="ghost"
                    className={cn(
                      "h-7 rounded px-2 text-[11px] capitalize",
                      groupBy === value && "bg-background shadow-sm",
                    )}
                    onClick={() => setGroupBy(value)}
                  >
                    {value}
                  </Button>
                ))}
              </div>
            ) : null}
          </div>
          {groupedRows.map((group) => {
            const groupCurrent = group.rows.reduce(
              (total, row) => total + row.current,
              0,
            );
            const groupNext = group.rows.reduce(
              (total, row) => total + getNextQuantity(row),
              0,
            );
            return (
              <div
                key={group.key}
                className="overflow-hidden rounded-lg border"
              >
                <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2.5">
                  <span className="min-w-0 truncate font-medium text-sm">
                    {group.title}
                  </span>
                  <span className="ml-auto text-muted-foreground text-xs">
                    {formatQuantity(groupCurrent)}
                    {groupNext !== groupCurrent
                      ? ` → ${formatQuantity(groupNext)}`
                      : ""}{" "}
                    {product.base_stock_uom_code}
                  </span>
                </div>
                <div className="divide-y">
                  {group.rows.map((row) => {
                    const rawValue = values[row.key];
                    const isTouched = touchedRowKeys.has(row.key);
                    const displayValue =
                      rawValue ??
                      (mode === "set_count"
                        ? String(
                            Number(
                              (row.current / selectedUomFactor).toFixed(4),
                            ),
                          )
                        : "");
                    const nextQuantity = getNextQuantity(row);
                    const delta = nextQuantity - row.current;
                    const error = getRowError(row);
                    const rowLabel =
                      groupBy === "warehouse" ? row.label : row.warehouse.name;
                    const ariaLabel =
                      row.productBrandId === undefined
                        ? `${row.warehouse.name} stock value`
                        : `${row.warehouse.name} ${row.label} stock value`;
                    return (
                      <div
                        key={row.key}
                        data-testid={
                          row.productBrandId === undefined
                            ? `warehouse-stock-row-${row.warehouse.id}`
                            : `stock-scope-row-${row.key}`
                        }
                        className="p-3 sm:grid sm:grid-cols-[minmax(100px,1fr)_auto] sm:items-center sm:gap-3 sm:px-3 sm:py-2.5"
                      >
                        <div className="min-w-0">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="min-w-0 truncate font-medium text-sm">
                              {rowLabel}
                            </span>
                            {row.productBrandId === null ? (
                              <Badge
                                variant="secondary"
                                className="shrink-0 rounded px-2 py-0.5 font-normal text-[10px]"
                              >
                                No brand
                              </Badge>
                            ) : null}
                          </div>
                          <p className="mt-0.5 text-muted-foreground text-xs">
                            Currently {formatQuantity(row.current)}{" "}
                            {product.base_stock_uom_code}
                            {row.reserved > 0
                              ? ` · ${formatQuantity(row.reserved)} reserved`
                              : ""}
                          </p>
                        </div>
                        <div className="mt-2 sm:mt-0">
                          <div className="flex items-center justify-end gap-2">
                            <Input
                              type="number"
                              step="0.01"
                              min={mode === "set_count" ? "0" : undefined}
                              aria-label={ariaLabel}
                              aria-invalid={Boolean(error)}
                              value={displayValue}
                              placeholder={
                                mode === "adjust_by" ? "+ / -" : undefined
                              }
                              disabled={!canManage}
                              className="h-10 min-w-0 flex-1 rounded-lg text-center font-medium sm:w-[72px] sm:flex-none"
                              onChange={(event) => {
                                setValues((currentValues) => ({
                                  ...currentValues,
                                  [row.key]: event.target.value,
                                }));
                                setTouchedRowKeys((currentKeys) => {
                                  const nextKeys = new Set(currentKeys);
                                  nextKeys.add(row.key);
                                  return nextKeys;
                                });
                              }}
                            />
                            <Badge
                              variant="secondary"
                              className="rounded px-2 py-1 text-[11px]"
                            >
                              {allowedUoms.find(
                                (uom) => String(uom.id) === uomId,
                              )?.code || product.base_stock_uom_code}
                            </Badge>
                            <span
                              className={cn(
                                "w-14 shrink-0 truncate text-right font-medium text-xs",
                                isTouched && delta > 0 && "text-emerald-500",
                                isTouched && delta < 0 && "text-rose-500",
                                (!isTouched || delta === 0) &&
                                  "text-muted-foreground",
                              )}
                            >
                              {!isTouched || delta === 0
                                ? "—"
                                : `${delta > 0 ? "+" : ""}${formatQuantity(delta)} ${product.base_stock_uom_code}`}
                            </span>
                          </div>
                          {error ? (
                            <p className="mt-1 text-right text-rose-500 text-xs">
                              {error}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-2">
          <div className="flex min-w-0 items-center gap-2">
            <label className="font-medium text-sm" htmlFor="stock-reason">
              Reason
            </label>
            <Badge
              variant="secondary"
              className="rounded px-2 py-0.5 text-[10px] text-rose-500"
            >
              Required
            </Badge>
          </div>
          <Select
            value={reason}
            onValueChange={(value: StockAdjustmentReason) => setReason(value)}
            disabled={!canManage}
          >
            <SelectTrigger id="stock-reason" className="h-11 rounded-lg">
              <SelectValue placeholder="Select a reason" />
            </SelectTrigger>
            <SelectContent>
              {REASONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="font-medium text-sm" htmlFor="stock-note">
            Note{" "}
            <span className="font-normal text-muted-foreground">
              (optional)
            </span>
          </label>
          <textarea
            id="stock-note"
            value={note}
            maxLength={1000}
            disabled={!canManage}
            className="min-h-20 w-full resize-y rounded-lg border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Add context for the audit history"
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        <div className="rounded-xl bg-muted/40 p-4">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">
              New total stock
            </span>
            <span className="ml-auto min-w-0 truncate font-medium text-sm">
              {formatQuantity(currentTotal)} → {formatQuantity(previewTotal)}{" "}
              {product.base_stock_uom_code}
            </span>
            {previewDelta !== 0 ? (
              <Badge
                className={cn(
                  "rounded px-2 py-0.5 text-[11px] text-white",
                  previewDelta > 0
                    ? "bg-emerald-500 hover:bg-emerald-500"
                    : "bg-rose-500 hover:bg-rose-500",
                )}
              >
                {previewDelta > 0 ? "+" : ""}
                {formatQuantity(previewDelta)}
              </Badge>
            ) : null}
          </div>
          <p className="mt-1 text-muted-foreground text-xs">
            {changedRows.length === 0
              ? "No warehouse quantities have changed."
              : `${changedRows.length} ${changedRows.length === 1 ? "warehouse" : "warehouses"} will be updated.`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 border-t bg-background px-5 py-4 sm:flex sm:justify-end sm:px-8 sm:py-5">
        <Button
          type="button"
          variant="outline"
          className="h-11 rounded-lg px-6"
          disabled={mutation.isPending}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className="h-11 rounded-lg px-6"
          disabled={!canManage || mutation.isPending}
          onClick={submit}
        >
          {mutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          {canManage ? "Save adjustment" : "Read-only access"}
        </Button>
      </div>
    </div>
  );
}

function HistoryRow({
  entry,
  fallbackUnit,
  canManage,
  onRevert,
  isReverting,
}: {
  entry: StockLedgerEntry;
  fallbackUnit: string;
  canManage: boolean;
  onRevert: () => void;
  isReverting: boolean;
}) {
  const delta = numberValue(entry.quantity_delta);
  const unit =
    entry.normalized_uom_code || entry.entered_uom_code || fallbackUnit;
  const sourceLabel = HISTORY_SOURCES.find(
    ([value]) => value === entry.source,
  )?.[1];
  const sourceDotClass =
    entry.source === "manual"
      ? "bg-emerald-900"
      : entry.source === "upload" || entry.source === "opening_balance"
        ? "bg-amber-300"
        : entry.source === "erp"
          ? "bg-cyan-300"
          : "bg-slate-300";
  const reason = entry.reason
    ? REASONS.find((item) => item.value === entry.reason)?.label ||
      entry.reason.split("_").join(" ")
    : null;
  const contextPrefix =
    entry.source === "upload"
      ? entry.upload_filename || reason
      : entry.source === "order"
        ? [entry.reference_id, entry.product_brand_name]
            .filter(Boolean)
            .join(" · ") || reason
        : reason;
  const context = [contextPrefix, entry.notes].filter(Boolean).join(" — ");
  const scopeLabel =
    entry.product_brand_name ||
    (entry.stock_scope === "unallocated" ? "Unbranded" : null);
  const timestamp = new Date(entry.created_at).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <div className="min-w-0 rounded-xl border px-4 py-3">
      <div className="flex min-w-0 items-center gap-2">
        <Badge
          variant="secondary"
          className="max-w-[60%] gap-1.5 overflow-hidden rounded px-2 py-1 font-medium text-[10px]"
        >
          <span className={cn("size-1.5 rounded-full", sourceDotClass)} />
          {sourceLabel || entry.source.split("_").join(" ")}
        </Badge>
        <div
          className={cn(
            "ml-auto min-w-0 truncate font-medium text-sm",
            delta > 0 && "text-emerald-500",
            delta < 0 && "text-rose-500",
          )}
        >
          {delta > 0 ? "+" : ""}
          {formatQuantity(delta)} {unit}
        </div>
      </div>
      <p className="mt-2 break-words font-medium text-xs">
        {entry.warehouse_name}
        {scopeLabel ? ` · ${scopeLabel}` : ""} ·{" "}
        {formatQuantity(entry.quantity_before)} →{" "}
        {formatQuantity(entry.quantity_after)} {unit}
      </p>
      {context ? (
        <p
          className="mt-1 truncate text-muted-foreground text-xs"
          title={context}
        >
          {context}
        </p>
      ) : null}
      <div className="mt-2 flex min-h-5 items-center gap-3">
        <span className="min-w-0 truncate text-[11px] text-muted-foreground">
          {entry.actor_name} · {timestamp}
        </span>
        <div className="flex-1" />
        {entry.upload_request_id ? (
          <a
            href={`/products/upload/review/${entry.upload_request_id}`}
            className="text-foreground text-xs hover:underline"
          >
            View batch
          </a>
        ) : null}
        {entry.reverted ? (
          <span className="text-muted-foreground text-xs">Reverted</span>
        ) : entry.can_revert && canManage ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-0 py-0 text-xs"
            disabled={isReverting}
            onClick={onRevert}
          >
            Revert
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function HistoryTab({
  product,
  warehouses,
  canManage,
  onReverted,
  onClose,
  isMobile,
}: {
  product: Product;
  warehouses: Warehouse[];
  canManage: boolean;
  onReverted: (product: Product) => void;
  onClose: () => void;
  isMobile: boolean;
}) {
  const queryClient = useQueryClient();
  const [warehouseId, setWarehouseId] = useState("all");
  const [productBrandId, setProductBrandId] = useState("all");
  const [source, setSource] = useState("all");
  const [historyPage, setHistoryPage] = useState(1);
  // Filters and product identity always restart history at the newest page.
  // biome-ignore lint/correctness/useExhaustiveDependencies: these values are intentional reset boundaries.
  useEffect(() => {
    setHistoryPage(1);
  }, [product.id, warehouseId, productBrandId, source]);
  const historyQuery = useQuery({
    queryKey: [
      "product-stock-history",
      product.id,
      warehouseId,
      productBrandId,
      source,
      historyPage,
    ],
    queryFn: () =>
      ProductService.listStockHistory(product.id, {
        warehouseId: warehouseId === "all" ? undefined : Number(warehouseId),
        productBrandId:
          productBrandId === "all"
            ? undefined
            : productBrandId === "unallocated"
              ? "unallocated"
              : Number(productBrandId),
        source,
        page: historyPage,
      }),
  });
  const revertMutation = useMutation({
    mutationFn: (adjustmentId: number) =>
      ProductService.createStockAdjustmentReversal(adjustmentId, {
        operation_id: crypto.randomUUID(),
        note: "",
      }),
    onSuccess: async () => {
      const refreshedProduct = await ProductService.retrieve(product.id);
      toast.success("The adjustment was reverted with a new audit entry.");
      onReverted(refreshedProduct);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["product-stock-history", product.id],
        }),
        queryClient.invalidateQueries({ queryKey: ["inventory-products"] }),
        queryClient.invalidateQueries({
          queryKey: ["inventory-product", product.id],
        }),
      ]);
    },
    onError: (error) =>
      toast.error(getErrorMessage(error, "Failed to revert this adjustment.")),
  });
  const exportUrl = ProductService.getStockHistoryExportUrl(product.id, {
    warehouseId: warehouseId === "all" ? undefined : Number(warehouseId),
    productBrandId:
      productBrandId === "all"
        ? undefined
        : productBrandId === "unallocated"
          ? "unallocated"
          : Number(productBrandId),
    source,
  });

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div className="min-w-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden px-5 py-5 sm:px-8">
        <div className="space-y-1">
          <h3 className="font-medium text-lg">Stock history</h3>
          <p className="text-muted-foreground text-sm leading-snug">
            Every change to this SKU&apos;s stock, newest first.
            <span className="hidden sm:inline">
              {" "}
              Uploads and ERP syncs keep a link back to the batch they came
              from.
            </span>
          </p>
        </div>
        {isMobile ? (
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {MOBILE_HISTORY_SOURCES.map(([value, label]) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={source === value ? "default" : "outline"}
                className="h-8 shrink-0 rounded-full px-3 text-xs"
                onClick={() => setSource(value)}
              >
                {label}
              </Button>
            ))}
          </div>
        ) : (
          <div
            className={cn(
              "grid min-w-0 gap-3",
              product.product_brands.length > 0 ? "grid-cols-3" : "grid-cols-2",
            )}
          >
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="h-10 w-full rounded-lg">
                <SelectValue placeholder="All sources" />
              </SelectTrigger>
              <SelectContent>
                {HISTORY_SOURCES.map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={warehouseId} onValueChange={setWarehouseId}>
              <SelectTrigger className="h-10 w-full rounded-lg">
                <SelectValue placeholder="All warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All warehouses</SelectItem>
                {warehouses.map((warehouse) => (
                  <SelectItem key={warehouse.id} value={String(warehouse.id)}>
                    {warehouse.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {product.product_brands.length > 0 ? (
              <Select value={productBrandId} onValueChange={setProductBrandId}>
                <SelectTrigger className="h-10 w-full rounded-lg">
                  <SelectValue placeholder="All brands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All brands</SelectItem>
                  {product.product_brands
                    .filter((brand) => brand.is_active)
                    .map((brand) => (
                      <SelectItem key={brand.id} value={String(brand.id)}>
                        {brand.brand_name}
                      </SelectItem>
                    ))}
                  <SelectItem value="unallocated">Unbranded</SelectItem>
                </SelectContent>
              </Select>
            ) : null}
          </div>
        )}
        {historyQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : historyQuery.isError ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center text-destructive">
            Failed to load stock history. Try again.
          </div>
        ) : historyQuery.data?.results.length ? (
          <div className="space-y-3">
            <div className="space-y-2.5">
              {historyQuery.data.results.map((entry) => (
                <HistoryRow
                  key={entry.id}
                  entry={entry}
                  fallbackUnit={product.base_stock_uom_code || "units"}
                  canManage={canManage}
                  isReverting={revertMutation.isPending}
                  onRevert={() => {
                    if (canManage && entry.stock_adjustment_id) {
                      revertMutation.mutate(entry.stock_adjustment_id);
                    }
                  }}
                />
              ))}
            </div>
            {historyQuery.data.previous || historyQuery.data.next ? (
              <div className="flex items-center gap-4">
                {historyQuery.data.next ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto px-0 py-1 text-xs"
                    disabled={
                      !historyQuery.data.next || historyQuery.isFetching
                    }
                    onClick={() => setHistoryPage((page) => page + 1)}
                  >
                    Load older changes
                  </Button>
                ) : null}
                {historyQuery.data.previous ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto px-0 py-1 text-xs"
                    disabled={historyQuery.isFetching}
                    onClick={() =>
                      setHistoryPage((page) => Math.max(page - 1, 1))
                    }
                  >
                    Back to newer changes
                  </Button>
                ) : null}
                <span className="ml-auto text-muted-foreground text-xs">
                  Page {historyPage}
                </span>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed p-8 text-center text-muted-foreground">
            No stock history matches these filters.
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 border-t bg-background px-5 py-4 sm:flex sm:justify-end sm:px-8 sm:py-5">
        <Button variant="outline" className="h-11 rounded-lg px-6" asChild>
          <a href={exportUrl} download>
            Export CSV
          </a>
        </Button>
        <Button
          type="button"
          className="h-11 rounded-lg px-6"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </div>
  );
}

export function ProductInventoryDrawer({
  open,
  onOpenChange,
  productId,
  initialTab,
  warehouses,
  uoms,
  canManage,
  isConfigureUomSubmitting,
  onConfigureUomSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: number | null;
  initialTab: ProductDrawerTab;
  warehouses: Warehouse[];
  uoms: UnitOfMeasure[];
  canManage: boolean;
  isConfigureUomSubmitting: boolean;
  onConfigureUomSubmit: (
    product: Product,
    payload: ConfigureProductUomPayload,
  ) => Promise<void>;
}) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<ProductDrawerTab>(initialTab);
  const [localProduct, setLocalProduct] = useState<Product | null>(null);
  const productQuery = useQuery({
    queryKey: ["inventory-product", productId],
    queryFn: () => ProductService.retrieve(productId as number),
    enabled: open && productId !== null,
  });

  useEffect(() => {
    if (open) {
      setTab(initialTab);
    }
  }, [open, initialTab]);

  useEffect(() => {
    if (productQuery.data) {
      setLocalProduct(productQuery.data);
    }
  }, [productQuery.data]);

  const product =
    localProduct?.id === productId ? localProduct : productQuery.data;
  const replaceProduct = (nextProduct: Product) => {
    setLocalProduct(nextProduct);
    queryClient.setQueryData(
      ["inventory-product", nextProduct.id],
      nextProduct,
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={cn(
          "max-w-full gap-0 overflow-x-hidden overflow-y-hidden bg-background p-0 [&_[data-slot=sheet-close]]:top-6 [&_[data-slot=sheet-close]]:right-6",
          isMobile
            ? "h-[92dvh] max-h-[92dvh] rounded-t-2xl [&_[data-slot=sheet-close]]:top-7 [&_[data-slot=sheet-close]]:right-4"
            : "w-full border-l sm:max-w-[552px]",
        )}
      >
        {isMobile ? (
          <div className="flex h-5 shrink-0 items-center justify-center">
            <span className="h-1 w-9 rounded-full bg-muted-foreground/30" />
          </div>
        ) : null}
        {product ? (
          <SheetHeader className="shrink-0 px-5 pt-5 pb-3 text-left sm:px-8 sm:pt-7 sm:pb-3">
            <ProductHeading product={product} />
          </SheetHeader>
        ) : null}
        {productQuery.isLoading && !product ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : product ? (
          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value as ProductDrawerTab)}
            className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-0 overflow-hidden"
          >
            <TabsList className="h-10 w-full shrink-0 justify-start gap-7 rounded-none border-b bg-transparent p-0 px-5 sm:px-8">
              {(["stock", "units", "history"] as const).map((value) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="h-10 flex-none rounded-none border-x-0 border-t-0 border-b-2 border-b-transparent px-0 py-2 font-normal text-muted-foreground capitalize shadow-none data-[state=active]:border-b-foreground data-[state=active]:bg-transparent data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  {value}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent
              value="stock"
              className="mt-0 flex min-h-0 w-full min-w-0 flex-1 overflow-hidden"
            >
              <StockTab
                product={product}
                warehouses={warehouses}
                uoms={uoms}
                canManage={canManage}
                onAdjusted={replaceProduct}
                onCancel={() => onOpenChange(false)}
              />
            </TabsContent>
            <TabsContent
              value="units"
              className="mt-0 flex min-h-0 w-full min-w-0 flex-1 overflow-hidden"
            >
              <ConfigureUomDrawer
                embedded
                canEdit={canManage}
                submitLabel={isMobile ? "Submit" : "Save units"}
                open={open && tab === "units"}
                onOpenChange={(nextOpen) => {
                  if (!nextOpen) {
                    onOpenChange(false);
                  }
                }}
                product={product}
                uoms={uoms}
                isSubmitting={isConfigureUomSubmitting}
                onSubmit={async (payload) => {
                  await onConfigureUomSubmit(product, payload);
                  const refreshed = await ProductService.retrieve(product.id);
                  replaceProduct(refreshed);
                }}
              />
            </TabsContent>
            <TabsContent
              value="history"
              className="mt-0 flex min-h-0 w-full min-w-0 flex-1 overflow-hidden"
            >
              <HistoryTab
                product={product}
                warehouses={warehouses}
                canManage={canManage}
                onReverted={replaceProduct}
                onClose={() => onOpenChange(false)}
                isMobile={isMobile}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="p-8 text-center text-destructive">
            Failed to load this product.
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
